/**
 * Normalize buy-source records from fixtures or /api/v1/buy-sources/* responses.
 * Backend ordering and economics remain authoritative — no re-ranking here.
 */

import { isFaviconServiceUrl } from "./merchantIcon";

const KNOWN_MERCHANT_DOMAINS = {
  target: "target.com",
  cvs: "cvs.com",
  walgreens: "walgreens.com",
  amazon: "amazon.com",
  "best buy": "bestbuy.com",
  bestbuy: "bestbuy.com",
  costco: "costco.com",
  iherb: "iherb.com",
  ebay: "ebay.com",
  mercari: "mercari.com",
  instacart: "instacart.com",
  doordash: "doordash.com",
  tiktok: "tiktok.com",
  "tiktok shop": "tiktok.com",
  facebook: "facebook.com",
  "facebook marketplace": "facebook.com",
  chewy: "chewy.com",
  ulta: "ulta.com",
  sephora: "sephora.com",
  "hy-vee": "hy-vee.com",
  hyvee: "hy-vee.com",
  "hy vee": "hy-vee.com",
  kroger: "kroger.com",
  meijer: "meijer.com",
  fareway: "fareway.com",
  "cash wise": "cashwise.com",
  cashwise: "cashwise.com",
};

/**
 * Never usable Buy Sources (exact/suffix only). Mirrors the backend policy:
 * payment/intermediary, delivery aggregators, peer-to-peer resale, social platforms.
 */
const EXCLUDED_BUY_SOURCE_DOMAINS = Object.freeze([
  "klarna.com",
  "blairimage.com",
  "doordash.com",
  "instacart.com",
  "instacart.ca",
  "mercari.com",
  "tiktok.com",
  "facebook.com",
]);

export function domainFromUrl(url) {
  if (!url) return "";
  try {
    return new URL(url).hostname.replace(/^www\./i, "");
  } catch {
    return "";
  }
}

export function normalizeBuySourceHostname(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  const host = raw.includes("://") ? domainFromUrl(raw) : raw;
  return String(host || "").replace(/\.+$/, "").replace(/^www\./, "");
}

/**
 * Centralized Buy Source domain exclusion.
 * Matches host and subdomains (klarna.com, www.klarna.com, pay.klarna.com)
 * but not lookalikes (notklarna.com, myblairimage.com).
 */
export function isExcludedBuySourceDomain(hostname) {
  const host = normalizeBuySourceHostname(hostname);
  if (!host) return false;
  return EXCLUDED_BUY_SOURCE_DOMAINS.some(
    (blocked) => host === blocked || host.endsWith(`.${blocked}`),
  );
}

export function isExcludedBuySource(raw) {
  if (!raw || typeof raw !== "object") return false;
  if (isExcludedBuySourceDomain(raw.source_domain)) return true;
  if (isExcludedBuySourceDomain(domainFromMerchantName(raw.source_name))) return true;
  for (const key of ["direct_url", "product_url", "merchant_offer_url", "source_url", "reference_url", "url"]) {
    if (isExcludedBuySourceDomain(raw[key])) return true;
  }
  return false;
}

export function isGoogleHost(domainOrUrl) {
  const raw = String(domainOrUrl || "").toLowerCase();
  if (!raw) return false;
  const host = raw.includes("://") ? domainFromUrl(raw) : raw.replace(/^www\./, "");
  return (
    host === "google.com"
    || host.endsWith(".google.com")
    || host.startsWith("google.")
    || host === "googleadservices.com"
    || host.endsWith(".googleadservices.com")
    || host === "googlesyndication.com"
    || host.endsWith(".googlesyndication.com")
  );
}

function isGoogleRelativePath(value) {
  const text = String(value || "").trim();
  if (!text || text.startsWith("http://") || text.startsWith("https://")) return false;
  const lower = text.toLowerCase();
  return (
    lower.startsWith("/search?")
    || lower.startsWith("/search/")
    || lower.startsWith("/shopping?")
    || lower.startsWith("/shopping/")
    || lower.startsWith("/url?")
    || lower.startsWith("/url/")
    || lower.startsWith("/goto?")
    || lower.startsWith("/goto/")
  );
}

function isUsableMerchantHref(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  if (isGoogleRelativePath(text)) return false;
  if (!(text.startsWith("http://") || text.startsWith("https://"))) return false;
  return !isGoogleHost(text);
}

export function domainFromMerchantName(name) {
  const raw = String(name || "").trim();
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (KNOWN_MERCHANT_DOMAINS[lower]) return KNOWN_MERCHANT_DOMAINS[lower];
  if (raw.includes(".") && !raw.includes(" ")) {
    const host = lower.replace(/^www\./, "");
    return isGoogleHost(host) ? "" : host;
  }
  for (const [key, domain] of Object.entries(KNOWN_MERCHANT_DOMAINS)) {
    if (lower === key || lower.startsWith(`${key} `) || lower.startsWith(`${key}-`)) {
      return domain;
    }
  }
  return "";
}

export function hostMatchesExpectedDomain(urlOrHost, expectedDomain) {
  const host = normalizeBuySourceHostname(urlOrHost);
  const expected = normalizeBuySourceHostname(expectedDomain);
  if (!host || !expected) return false;
  if (isGoogleHost(host) || isExcludedBuySourceDomain(host)) return false;
  return host === expected || host.endsWith(`.${expected}`);
}

function isExplicitDirectLink(raw) {
  const flag = raw?.direct_link;
  return flag === true || flag === 1 || flag === "true" || flag === "1";
}

/**
 * Canonical trusted merchant PDP URL for navigation.
 *
 * ONLY returns a URL when:
 * - direct_url exists
 * - direct_link === true
 * - http/https
 * - not Google / not excluded intermediary
 * - hostname matches expected merchant domain when known
 *
 * NEVER falls back to reference_url, google_offer_url, google_product_url,
 * Google /goto, or a synthesized domain URL.
 */
export function trustedMerchantDirectUrl(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (isExcludedBuySource(raw)) return null;
  if (!isExplicitDirectLink(raw)) return null;

  const direct = String(raw.direct_url || "").trim();
  if (!isUsableMerchantHref(direct)) return null;
  if (isExcludedBuySourceDomain(direct)) return null;

  const expected =
    normalizeBuySourceHostname(raw.source_domain)
    || domainFromMerchantName(raw.source_name);
  if (expected) {
    if (!hostMatchesExpectedDomain(direct, expected)) return null;
  } else {
    // No merchant identity — still reject Google / excluded hosts (already gated).
    const host = domainFromUrl(direct);
    if (!host || isGoogleHost(host) || isExcludedBuySourceDomain(host)) return null;
  }

  return direct;
}

const GOOGLE_NAV_HOST_RE = /^(?:www\.)?google\.(?:com|[a-z]{2}|co\.[a-z]{2}|com\.[a-z]{2})$/;
const GOOGLE_NAV_PATH_RE = /^\/(?:search|shopping)(?:\/|$)/;
// eslint-disable-next-line no-control-regex
const UNSAFE_URL_CHARS_RE = /[\s\u0000-\u001f\u007f]/;

// "I'm Feeling Lucky" jumps straight to the first result's site.
const GOOGLE_NAV_FORBIDDEN_PARAMS = new Set(["btni"]);

/**
 * Google Search/Shopping page that is safe to open.
 * Rejects redirectors (/url, /goto, /aclk), ad hosts, btnI, credentials and ports.
 */
export function safeGoogleNavigationUrl(value) {
  const text = String(value || "").trim();
  if (!text.startsWith("https://") || UNSAFE_URL_CHARS_RE.test(text)) return null;
  let parsed;
  try {
    parsed = new URL(text);
  } catch {
    return null;
  }
  if (parsed.username || parsed.password || parsed.port) return null;
  if (!GOOGLE_NAV_HOST_RE.test(parsed.hostname.toLowerCase())) return null;
  if (!GOOGLE_NAV_PATH_RE.test(parsed.pathname)) return null;
  for (const key of parsed.searchParams.keys()) {
    if (GOOGLE_NAV_FORBIDDEN_PARAMS.has(key.trim().toLowerCase())) return null;
  }
  return text;
}

export const LINK_TYPE_DIRECT = "direct";
export const LINK_TYPE_GOOGLE_RESULT = "google_result";
export const LINK_TYPE_GOOGLE_SEARCH = "google_search_generated";
export const LINK_TYPE_NONE = "none";
const LEGACY_LINK_TYPE_GOOGLE = "google";

/**
 * Where a Buy Source card navigates: this row's trusted merchant PDP, else
 * this row's own provider Google offer/product/reference page, else the
 * backend-generated Google search for this row, else nothing.
 * Never synthesizes a merchant URL from the merchant name or domain.
 */
export function buySourceNavigation(raw) {
  if (!raw || typeof raw !== "object" || isExcludedBuySource(raw)) {
    return { href: null, type: LINK_TYPE_NONE };
  }
  const direct = trustedMerchantDirectUrl(raw);
  if (direct) return { href: direct, type: LINK_TYPE_DIRECT };
  const type = raw.navigation_link_type;
  if (type === LINK_TYPE_GOOGLE_RESULT || type === LEGACY_LINK_TYPE_GOOGLE) {
    const resolved = safeGoogleNavigationUrl(raw.navigation_url);
    if (resolved) return { href: resolved, type: LINK_TYPE_GOOGLE_RESULT };
  }
  for (const key of ["google_offer_url", "google_product_url", "reference_url"]) {
    const safe = safeGoogleNavigationUrl(raw[key]);
    if (safe) return { href: safe, type: LINK_TYPE_GOOGLE_RESULT };
  }
  if (type === LINK_TYPE_GOOGLE_SEARCH) {
    const generated = safeGoogleNavigationUrl(raw.navigation_url);
    if (generated) return { href: generated, type: LINK_TYPE_GOOGLE_SEARCH };
  }
  return { href: null, type: LINK_TYPE_NONE };
}

/**
 * Display merchant identity — never prefer google.com from offer navigation URLs.
 */
export function resolveDisplayDomain(raw) {
  const explicit = String(raw?.source_domain || "").trim().replace(/^www\./i, "");
  if (explicit && !isGoogleHost(explicit)) return explicit;

  const fromName = domainFromMerchantName(raw?.source_name);
  if (fromName) return fromName;

  const sourceUrl = raw?.source_url || raw?.url || "";
  const fromUrl = domainFromUrl(sourceUrl);
  if (fromUrl && !isGoogleHost(fromUrl)) return fromUrl;

  const name = String(raw?.source_name || "").trim();
  return name || "source";
}

/**
 * Deprecated: Storefront Hunter no longer fetches remote favicons.
 *
 * Kept as a stub so existing imports keep working, but it always returns null —
 * merchants render from the local badge map (see merchantIcon.js). Calling an
 * external favicon service produced a request storm (one per card per render).
 */
export function faviconUrlForDomain(_domain) {
  return null;
}

export function hasPreviewToken(source) {
  return !!(source?.preview_token || source?.preview?.token);
}

/**
 * Valid product image src for Buy Source thumbs / hover preview.
 * Accepts http(s) and data:image/{webp,png,jpeg,jpg,gif}; rejects malformed data URLs.
 */
export function isValidProductImageSrc(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  if (text.startsWith("https://") || text.startsWith("http://")) return true;
  const m = text.match(/^data:image\/(webp|png|jpe?g|gif);base64,([A-Za-z0-9+/=\s]+)$/i);
  if (!m) return false;
  const payload = m[2].replace(/\s+/g, "");
  return payload.length >= 8;
}

/**
 * Only trusted merchant direct_url is clickable.
 * Never fall back to google_offer_url / reference_url / google_product_url.
 */
export function resolveExternalHref(raw) {
  return trustedMerchantDirectUrl(raw);
}

export function normalizeBuySource(raw) {
  if (!raw) return null;
  if (isExcludedBuySource(raw)) return null;

  const referenceUrl = raw.reference_url || raw.google_offer_url || raw.google_product_url || null;
  const googleOfferUrl =
    referenceUrl && isGoogleHost(referenceUrl)
      ? referenceUrl
      : raw.google_offer_url && isGoogleHost(raw.google_offer_url)
        ? raw.google_offer_url
        : raw.google_product_url && isGoogleHost(raw.google_product_url)
          ? raw.google_product_url
          : null;

  const trusted = trustedMerchantDirectUrl(raw);
  // Never promote product_url / source_url / reference into a clickable direct_url.
  const rawDirect = String(raw.direct_url || "").trim();
  const storedDirect = trusted || (isUsableMerchantHref(rawDirect) && isExplicitDirectLink(raw) ? rawDirect : null);

  const sourceDomain = resolveDisplayDomain(raw);
  const rawImage = raw.image_url || raw.product_image || null;
  const imageUrl = isValidProductImageSrc(rawImage) ? rawImage : null;
  // Remote favicons are never used. A provider-supplied icon is only kept when
  // it is a real merchant asset, not a favicon-service URL.
  const providerFavicon =
    [
      raw.favicon_url,
      raw.faviconUrl,
      raw.site_icon,
      raw.icon_url,
    ].find((value) => {
      const text = String(value || "").trim();
      if (!(text.startsWith("http://") || text.startsWith("https://"))) return false;
      return !isFaviconServiceUrl(text);
    }) || null;
  const faviconUrl = providerFavicon;

  const observedPurchasePrice =
    raw.observed_purchase_price ?? (raw.price != null && raw.display_cost == null ? raw.price : null);

  const targetEquivalentCost =
    raw.display_cost ??
    raw.target_equivalent_cost ??
    raw.target_equivalent ??
    null;

  const previewToken = raw.preview?.token ?? raw.preview_token ?? null;
  const clickHref = trusted;
  const navigation = buySourceNavigation(raw);

  // Candidate title from Google/Oxylabs — never overwrite with Walmart target title.
  const candidateTitle =
    raw.candidate_title || raw.title || raw.product_title || raw.short_title || "";

  return {
    id:
      raw.id ||
      `${sourceDomain}-${storedDirect || "no-url"}-${targetEquivalentCost ?? observedPurchasePrice}`,
    source_name: raw.source_name || sourceDomain,
    source_domain: sourceDomain,
    // Never keep Google navigation URLs as source_url (would leak into preview overlays).
    source_url: storedDirect || "",
    direct_url: storedDirect,
    product_url: storedDirect,
    merchant_offer_url: storedDirect,
    reference_url: googleOfferUrl || (referenceUrl && isGoogleHost(referenceUrl) ? referenceUrl : null),
    google_offer_url: googleOfferUrl,
    google_product_url:
      raw.google_product_url && isGoogleHost(raw.google_product_url)
        ? String(raw.google_product_url)
        : null,
    direct_link: Boolean(clickHref),
    title: candidateTitle,
    candidate_title: candidateTitle,
    product_title: candidateTitle,
    image_url: imageUrl && imageUrl !== faviconUrl ? imageUrl : imageUrl,
    favicon_url: faviconUrl,
    faviconUrl: faviconUrl,
    observed_purchase_price: observedPurchasePrice,
    display_cost: raw.display_cost ?? targetEquivalentCost,
    target_equivalent_cost: targetEquivalentCost,
    display_cost_basis: raw.display_cost_basis || null,
    verification_badge: raw.verification_badge || raw.verification_status || null,
    verification_status: raw.verification_status || raw.verification_badge || null,
    difference_vs_walmart: raw.difference_vs_walmart ?? raw.price_difference ?? null,
    preview: previewToken ? { token: previewToken } : null,
    preview_token: previewToken,
    external_href: clickHref,
    navigation_url: navigation.href,
    navigation_link_type: navigation.type,
    price: targetEquivalentCost ?? observedPurchasePrice ?? raw.price ?? null,
  };
}

export function normalizeBuySources(list) {
  return (list || []).map(normalizeBuySource).filter(Boolean);
}

/** Count clickable cards — must stay ≤ backend direct_url_count. */
export function countClickableBuySources(list) {
  return (list || []).reduce((n, row) => (trustedMerchantDirectUrl(row) ? n + 1 : n), 0);
}
